import {
	type AssetType,
	fallbackToEmptyArray,
	fallbackToEmptyObject,
	type Form,
	type IssueType,
	type ProcedureInitiative,
	type ProcedureType,
} from "@overmap-ai/common"
import { restructureSelectorWithArgs } from "@overmap-ai/core"
import { createSelector } from "@reduxjs/toolkit"
import type { SortingState, VisibilityState } from "@tanstack/react-table"

import type { AnySerializedFilter } from "../../filtering/filter"
import type { RootSelector, RootSelectorWithArgs, RootState } from "../typings"

const defaultSubmittedAtColumnSorting: SortingState = [
	{
		id: "submitted_at",
		desc: true,
	},
]

export const selectAssetTypeFiltersMapping: RootSelector<Record<AssetType["uuid"], AnySerializedFilter[]>> = (
	state,
) => {
	return state.filters.assetTypeFilters
}

export const selectAssetTypeFiltersById: RootSelectorWithArgs<AssetType["uuid"], AnySerializedFilter[]> =
	restructureSelectorWithArgs(
		createSelector([selectAssetTypeFiltersMapping, (_, id: AssetType["uuid"]) => id], (assetTypeFilters, id) => {
			return fallbackToEmptyArray(assetTypeFilters[id] ?? [])
		}),
	)

export const selectFormFiltersMapping: RootSelector<Record<Form["uuid"], AnySerializedFilter[]>> = (state) => {
	return state.filters.formSubmissionFilters
}

export const selectFormFiltersOfForm: RootSelectorWithArgs<Form["uuid"], AnySerializedFilter[]> =
	restructureSelectorWithArgs(
		createSelector([selectFormFiltersMapping, (_, id: Form["uuid"]) => id], (formFilters, id) => {
			return fallbackToEmptyArray(formFilters[id] ?? [])
		}),
	)

export const selectIssueTypeFiltersMapping: RootSelector<Record<string, AnySerializedFilter[]>> = (state) => {
	return state.filters.issueTypeFilters
}

export const selectIssueTypeFiltersById: RootSelectorWithArgs<IssueType["uuid"], AnySerializedFilter[]> =
	restructureSelectorWithArgs(
		createSelector([selectIssueTypeFiltersMapping, (_, id: IssueType["uuid"]) => id], (issueTypeFilters, id) => {
			return fallbackToEmptyArray(issueTypeFilters[id] ?? [])
		}),
	)

export const selectFormFilters = createSelector([(state: RootState) => state.filters.formFilters], (filters) => {
	return filters
})

export const selectProjectFilters = createSelector([(state: RootState) => state.filters.projectFilters], (filters) => {
	return filters
})

export const selectProcedureInitiativeDataFiltersMapping: RootSelector<
	Record<ProcedureInitiative["uuid"], AnySerializedFilter[]>
> = (state) => {
	return state.filters.procedureInitiativeDataFilters
}

export const selectProcedureInitiativeDataFiltersOfProcedureInitiative: RootSelectorWithArgs<
	ProcedureInitiative["uuid"],
	AnySerializedFilter[]
> = restructureSelectorWithArgs(
	createSelector(
		[
			selectProcedureInitiativeDataFiltersMapping,
			(_, procedureInitiativeId: ProcedureInitiative["uuid"]) => procedureInitiativeId,
		],
		(mapping, procedureInitiativeId) => {
			return fallbackToEmptyArray(mapping[procedureInitiativeId] ?? [])
		},
	),
)

export const selectProcedureTypeDataFiltersMapping: RootSelector<
	Record<ProcedureType["uuid"], AnySerializedFilter[]>
> = (state) => {
	return state.filters.procedureTypeDataFilters
}

export const selectProcedureTypeDataFiltersOfProcedureType: RootSelectorWithArgs<
	ProcedureType["uuid"],
	AnySerializedFilter[]
> = restructureSelectorWithArgs(
	createSelector(
		[selectProcedureTypeDataFiltersMapping, (_, procedureTypeId: ProcedureType["uuid"]) => procedureTypeId],
		(mapping, procedureTypeId) => {
			return fallbackToEmptyArray(mapping[procedureTypeId] ?? [])
		},
	),
)

export const selectProcedureInitiativeFilters = createSelector(
	[(state: RootState) => state.filters.procedureInitiativeFilters],
	(filters) => {
		return filters
	},
)

export const selectAssetTypeColumnVisibilityMapping: RootSelector<Record<AssetType["uuid"], VisibilityState>> = (
	state,
) => {
	return state.filters.assetTypeColumnVisibility
}

export const selectColumnVisibilityOfAssetType: RootSelectorWithArgs<AssetType["uuid"], VisibilityState> =
	restructureSelectorWithArgs(
		createSelector(
			[selectAssetTypeColumnVisibilityMapping, (_, assetTypeId: AssetType["uuid"]) => assetTypeId],
			(assetTypeColumnVisibility, assetTypeId) => {
				return fallbackToEmptyObject(assetTypeColumnVisibility[assetTypeId] ?? {})
			},
		),
	)

export const selectAssetTypeColumnSortingMapping: RootSelector<Record<AssetType["uuid"], SortingState>> = (state) => {
	return state.filters.assetTypeColumnSorting
}

export const selectColumnSortingOfAssetType: RootSelectorWithArgs<AssetType["uuid"], SortingState> =
	restructureSelectorWithArgs(
		createSelector(
			[selectAssetTypeColumnSortingMapping, (_, assetTypeId: AssetType["uuid"]) => assetTypeId],
			(assetTypeColumnSorting, assetTypeId) => {
				return fallbackToEmptyArray(assetTypeColumnSorting[assetTypeId] ?? [])
			},
		),
	)

export const selectIssueTypeColumnVisibilityMapping: RootSelector<Record<IssueType["uuid"], VisibilityState>> = (
	state,
) => {
	return state.filters.issueTypeColumnVisibility
}

export const selectColumnVisibilityOfIssueType: RootSelectorWithArgs<IssueType["uuid"], VisibilityState> =
	restructureSelectorWithArgs(
		createSelector(
			[selectIssueTypeColumnVisibilityMapping, (_, issueTypeId: IssueType["uuid"]) => issueTypeId],
			(issueTypeColumnVisibility, issueTypeId) => {
				return fallbackToEmptyObject(issueTypeColumnVisibility[issueTypeId] ?? {})
			},
		),
	)

export const selectIssueTypeColumnSortingMapping: RootSelector<Record<IssueType["uuid"], SortingState>> = (state) => {
	return state.filters.issueTypeColumnSorting
}

export const selectColumnSortingOfIssueType: RootSelectorWithArgs<IssueType["uuid"], SortingState> =
	restructureSelectorWithArgs(
		createSelector(
			[selectIssueTypeColumnSortingMapping, (_, issueTypeId: IssueType["uuid"]) => issueTypeId],
			(issueTypeColumnSorting, issueTypeId) => {
				return Object.prototype.hasOwnProperty.call(issueTypeColumnSorting, issueTypeId)
					? fallbackToEmptyArray(issueTypeColumnSorting[issueTypeId] ?? [])
					: defaultSubmittedAtColumnSorting
			},
		),
	)

export const selectFormSubmissionColumnVisibilityMapping: RootSelector<Record<Form["uuid"], VisibilityState>> = (
	state,
) => {
	return state.filters.formSubmissionColumnVisibility
}

export const selectColumnVisibilityOfForm: RootSelectorWithArgs<Form["uuid"], VisibilityState> =
	restructureSelectorWithArgs(
		createSelector(
			[selectFormSubmissionColumnVisibilityMapping, (_, formId: Form["uuid"]) => formId],
			(formSubmissionColumnVisibility, formId) => {
				return fallbackToEmptyObject(formSubmissionColumnVisibility[formId] ?? {})
			},
		),
	)

export const selectFormSubmissionColumnSortingMapping: RootSelector<Record<Form["uuid"], SortingState>> = (state) => {
	return state.filters.formSubmissionColumnSorting
}

export const selectColumnSortingOfForm: RootSelectorWithArgs<Form["uuid"], SortingState> = restructureSelectorWithArgs(
	createSelector(
		[selectFormSubmissionColumnSortingMapping, (_, formId: Form["uuid"]) => formId],
		(formSubmissionColumnSorting, formId) => {
			return fallbackToEmptyArray(formSubmissionColumnSorting[formId] ?? [])
		},
	),
)

export const selectProcedureInitiativeDataColumnVisibilityMapping: RootSelector<
	Record<ProcedureInitiative["uuid"], VisibilityState>
> = (state) => {
	return state.filters.procedureInitiativeDataColumnVisibility
}

export const selectColumnVisibilityOfProcedureInitiativeData: RootSelectorWithArgs<
	ProcedureInitiative["uuid"],
	VisibilityState
> = restructureSelectorWithArgs(
	createSelector(
		[
			selectProcedureInitiativeDataColumnVisibilityMapping,
			(_, procedureInitiativeId: ProcedureInitiative["uuid"]) => procedureInitiativeId,
		],
		(procedureInitiativeDataColumnVisibility, procedureInitiativeId) => {
			return fallbackToEmptyObject(procedureInitiativeDataColumnVisibility[procedureInitiativeId] ?? {})
		},
	),
)

export const selectProcedureInitiativeDataColumnSortingMapping: RootSelector<
	Record<ProcedureInitiative["uuid"], SortingState>
> = (state) => {
	return state.filters.procedureInitiativeDataColumnSorting
}

export const selectColumnSortingOfProcedureInitiativeData: RootSelectorWithArgs<
	ProcedureInitiative["uuid"],
	SortingState
> = restructureSelectorWithArgs(
	createSelector(
		[
			selectProcedureInitiativeDataColumnSortingMapping,
			(_, procedureInitiativeId: ProcedureInitiative["uuid"]) => procedureInitiativeId,
		],
		(procedureInitiativeDataColumnSorting, procedureInitiativeId) => {
			return fallbackToEmptyArray(procedureInitiativeDataColumnSorting[procedureInitiativeId] ?? [])
		},
	),
)

export const selectProcedureTypeDataColumnVisibilityMapping: RootSelector<
	Record<ProcedureType["uuid"], VisibilityState>
> = (state) => {
	return state.filters.procedureTypeDataColumnVisibility
}

export const selectColumnVisibilityOfProcedureTypeData: RootSelectorWithArgs<ProcedureType["uuid"], VisibilityState> =
	restructureSelectorWithArgs(
		createSelector(
			[
				selectProcedureTypeDataColumnVisibilityMapping,
				(_, procedureTypeId: ProcedureType["uuid"]) => procedureTypeId,
			],
			(procedureTypeDataColumnVisibility, procedureTypeId) => {
				return fallbackToEmptyObject(procedureTypeDataColumnVisibility[procedureTypeId] ?? {})
			},
		),
	)

export const selectProcedureTypeDataColumnSortingMapping: RootSelector<Record<ProcedureType["uuid"], SortingState>> = (
	state,
) => {
	return state.filters.procedureTypeDataColumnSorting
}

export const selectColumnSortingOfProcedureTypeData: RootSelectorWithArgs<ProcedureType["uuid"], SortingState> =
	restructureSelectorWithArgs(
		createSelector(
			[
				selectProcedureTypeDataColumnSortingMapping,
				(_, procedureTypeId: ProcedureType["uuid"]) => procedureTypeId,
			],
			(procedureTypeDataColumnSorting, procedureTypeId) => {
				return fallbackToEmptyArray(procedureTypeDataColumnSorting[procedureTypeId] ?? [])
			},
		),
	)
